import { type KeyboardEvent, useContext, useMemo, useState } from 'react'

import {
  Button,
  Checkbox,
  IconArrowLineDown,
  IconButton,
  IconCloudArrowUp,
  IconCube,
  IconLinkSimple,
  IconMagnifyingGlass,
  IconPlus,
  IconTrash,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip
} from '@vtex/shoreline'

import { FeedbackContext } from '~/components/feedback'
import PageTitle from '~/components/PageTitle'
import { isReadOnly } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { BulkBar } from '../../components/advertiserBrand/BulkBar'
import { CatalogListCard } from '../../components/advertiserBrand/CatalogListCard'
import { NtbGate } from '../../components/advertiserBrand/NtbGate'
import { PageEmptyState } from '../../components/advertiserBrand/PageEmptyState'
import { PageHeader } from '../../components/advertiserBrand/PageHeader'
import { ProductThumb } from '../../components/advertiserBrand/ProductThumb'
import { SearchFilter } from '../../components/advertiserBrand/SearchFilter'
import styles from '../../components/advertiserBrand/styles.module.css'
import { UnassignedSuggestionsBanner } from '../../components/advertiserBrand/UnassignedSuggestionsBanner'
import { UnassignedSuggestionsModal } from '../../components/advertiserBrand/UnassignedSuggestionsModal'
import { useConfirmDelete } from '../../components/advertiserBrand/useConfirmDelete'
import { useHotkey } from '../../components/advertiserBrand/useHotkey'
import { usePushSearchParams } from '../../components/advertiserBrand/usePushSearchParams'
import { useRowSelection } from '../../components/advertiserBrand/useRowSelection'
import {
  downloadAdvertiserProductsCsv,
  EXPORT_MAX_ROWS,
  fetchAllAdvertiserProducts
} from '../../helpers/advertiserBrandCsv'
import { productCode, productKey, toIdentities } from '../../helpers/gtin'
import {
  useAdvertiserBrandCatalogSearch,
  useAdvertiserBrandProductMutations,
  useAdvertiserBrandProducts
} from '../../hooks/useAdvertiserBrandProducts'
import { useNtbMetricsGate } from '../../hooks/useNtbMetricsEnabled'
import { AddProductsModal } from './elements/AddProductsModal'
import { ImportCsvModal } from './elements/ImportCsvModal'
import { LinkBrandModal } from './elements/LinkBrandModal'
import { useProductDrawer } from './elements/ProductDrawer'
import { ProductsFilters } from './elements/ProductsFilters'

// seleção · produto · marca · ações
// a marca cresce com o nome (teto em `.brandCell`); o produto fica com a sobra
const COLUMN_WIDTHS = ['2.5rem', 'minmax(0, 1fr)', 'minmax(11rem, max-content)']
const ACTIONS_COLUMN_WIDTH = '3.25rem'
const QUANTITY = 25

const getId = (product: AdvertiserBrandProduct) => product.id

/** Enter/Espaço abrem a linha; teclas em checkbox e botões seguem sendo deles. */
const isRowActivateKey = (event: KeyboardEvent) =>
  event.target === event.currentTarget &&
  (event.key === 'Enter' || event.key === ' ')

const AdvertiserProductsPage: React.FC = () => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const queryParams = useQueryParams()
  const pushParams = usePushSearchParams()
  const page = Number(queryParams.get('page')) || 1
  const query = queryParams.get('query') || undefined
  const brands = queryParams.getAll('brand')
  const unassigned = queryParams.get('unassigned') === 'true' || undefined

  // o mesmo gate que decide o corpo da página segura as chamadas à API
  const { isEnabled: isNtbEnabled } = useNtbMetricsGate()

  const { data, isPending, error } = useAdvertiserBrandProducts(
    {
      page,
      quantity: QUANTITY,
      query,
      brand: brands,
      unassigned
    },
    { enabled: isNtbEnabled }
  )
  const { removeProducts } = useAdvertiserBrandProductMutations()

  const products = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.total ?? 0

  // uma chamada para os EANs da página e uma por publisher das linhas por SKU
  const { catalogByKey } = useAdvertiserBrandCatalogSearch(products)

  const selection = useRowSelection(products, getId)
  const confirmRemove = useConfirmDelete<AdvertiserBrandProduct>()
  const openProductDrawer = useProductDrawer()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isBannerDismissed, setIsBannerDismissed] = useState(false)
  const [isUnassignedSuggestionsOpen, setIsUnassignedSuggestionsOpen] =
    useState(false)

  const selectedProducts = selection.selectedRows

  const handleRemove = async () => {
    await removeProducts.mutateAsync(toIdentities(confirmRemove.items))

    // remover pelo kebab preserva a seleção, menos as linhas que acabaram de sair
    if (confirmRemove.fromSelection) selection.clear()
    else selection.deselect(confirmRemove.items.map(getId))

    confirmRemove.close()
  }

  /** Exporta a seleção ou, sem ela, a listagem inteira com os filtros atuais. */
  const handleExport = async (rows?: AdvertiserBrandProduct[]) => {
    setIsExporting(true)

    try {
      const exported =
        rows ??
        (await fetchAllAdvertiserProducts({
          query,
          brand: brands,
          unassigned
        }))

      downloadAdvertiserProductsCsv(
        exported,
        rows ? 'produtos-selecionados.csv' : 'meus-produtos.csv'
      )

      // a listagem completa para no teto de linhas
      if (!rows && total > EXPORT_MAX_ROWS) {
        toast.warning(
          t('rm:advertiserProducts.export.truncated', {
            max: EXPORT_MAX_ROWS
          })
        )
      }
    } catch {
      toast.critical(t('rm:advertiserProducts.export.error'))
    } finally {
      setIsExporting(false)
    }
  }

  const canManage = !isReadOnly()
  const columnWidths = canManage
    ? [...COLUMN_WIDTHS, ACTIONS_COLUMN_WIDTH]
    : COLUMN_WIDTHS

  useHotkey(
    event => event.shiftKey && event.key.toLowerCase() === 'p',
    () => setIsAddOpen(true),
    { enabled: canManage }
  )

  const hasFilters = !!brands.length || !!unassigned
  const countLabel = selection.selected.length
    ? t('rm:advertiserProducts.selected', { count: selection.selected.length })
    : t('rm:advertiserProducts.count', { count: total })

  const emptyFilteredDescription = query
    ? t('rm:advertiserProducts.emptySearch.description', { query })
    : t('rm:advertiserProducts.emptyFiltered.description')

  const addProductsLabel = `${t('rm:advertiserProducts.cta.addProducts')} (⇧P)`

  const linkAction = {
    id: 'link',
    label: t('rm:advertiserProducts.bulk.linkBrand'),
    icon: <IconLinkSimple />,
    onClick: () => setIsLinkOpen(true)
  }

  const exportAction = {
    id: 'export',
    label: t('rm:advertiserProducts.bulk.export'),
    icon: <IconArrowLineDown />,
    disabled: isExporting,
    onClick: () => handleExport(selectedProducts)
  }

  const removeAction = {
    id: 'remove',
    label: t('rm:advertiserProducts.bulk.remove'),
    icon: <IconTrash />,
    critical: true,
    onClick: () => confirmRemove.ask(selectedProducts, { fromSelection: true })
  }

  // ler não dá direito de vincular nem remover; exportar a seleção continua valendo
  const bulkActions = canManage
    ? [linkAction, exportAction, removeAction]
    : [exportAction]

  const addButton = (
    <Button variant="primary" onClick={() => setIsAddOpen(true)}>
      <IconPlus />
      {t('rm:advertiserProducts.cta.addProducts')}
    </Button>
  )

  return (
    <div className={styles.page}>
      <PageTitle title={t('rm:advertiserProducts.pageTitle')} />

      <PageHeader
        title={t('rm:advertiserProducts.pageTitle')}
        actions={
          isNtbEnabled && (
            <>
              <SearchFilter
                placeholder={t('rm:advertiserProducts.search.placeholder')}
              />

              <ProductsFilters value={{ brands, unassigned }} />

              {canManage && (
                <Tooltip label={addProductsLabel}>
                  <IconButton
                    variant="secondary"
                    label={addProductsLabel}
                    onClick={() => setIsAddOpen(true)}
                  >
                    <IconPlus />
                  </IconButton>
                </Tooltip>
              )}

              <Menu
                label={t('rm:advertiserProducts.cta.moreActions')}
                type="actions"
                iconOnly
              >
                {canManage && (
                  <MenuItem onClick={() => setIsImportOpen(true)}>
                    <IconCloudArrowUp />
                    {t('rm:advertiserProducts.cta.import')}
                  </MenuItem>
                )}
                <MenuItem disabled={isExporting} onClick={() => handleExport()}>
                  <IconArrowLineDown />
                  {t('rm:advertiserProducts.cta.export')}
                </MenuItem>
              </Menu>
            </>
          )
        }
      />

      <NtbGate>
        {canManage && !isBannerDismissed && (
          <UnassignedSuggestionsBanner
            onOpen={() => setIsUnassignedSuggestionsOpen(true)}
            onDismiss={() => setIsBannerDismissed(true)}
          />
        )}

        <CatalogListCard
          isPending={isPending}
          isEmpty={!products.length}
          error={error}
          hasFilters={hasFilters || !!query}
          empty={
            <PageEmptyState
              icon={<IconCube width={28} height={28} />}
              title={t('rm:advertiserProducts.empty.title')}
              description={t('rm:advertiserProducts.empty.description')}
              action={canManage ? addButton : undefined}
            />
          }
          emptyFiltered={
            <PageEmptyState
              icon={<IconMagnifyingGlass width={28} height={28} />}
              title={t('rm:advertiserProducts.emptyFiltered.title')}
              description={emptyFilteredDescription}
              action={
                <Button
                  variant="tertiary"
                  onClick={() =>
                    pushParams(params => {
                      // sem filtro no drawer, o CTA limpa apenas a busca
                      if (query) params.delete('query')

                      if (!query || hasFilters) {
                        params.delete('brand')
                        params.delete('unassigned')
                      }

                      params.set('page', '1')
                    })
                  }
                >
                  {t(
                    query && !hasFilters
                      ? 'rm:advertiserProducts.filters.clearSearch'
                      : 'rm:advertiserProducts.filters.clear'
                  )}
                </Button>
              }
            />
          }
          countLabel={countLabel}
          page={page}
          pageSize={QUANTITY}
          total={total}
          onPageChange={nextPage =>
            pushParams(params => params.set('page', String(nextPage)))
          }
        >
          <Table columnWidths={columnWidths}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  <Checkbox
                    checked={selection.allSelected}
                    indeterminate={
                      !selection.allSelected && selection.someSelected
                    }
                    onChange={selection.toggleAll}
                    aria-label={t('rm:advertiserCatalog.selectAll')}
                  />
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('rm:advertiserProducts.table.column.product')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('rm:advertiserProducts.table.column.brand')}
                </TableHeaderCell>
                {canManage && <TableHeaderCell />}
              </TableRow>
            </TableHeader>

            <TableBody>
              {products.map(product => {
                const catalog = catalogByKey[productKey(product)]
                const code = productCode(product)
                const name = catalog?.name || product.product_name || code

                const openDrawer = () =>
                  openProductDrawer({
                    product,
                    name,
                    imageUrl: catalog?.image_url
                  })

                return (
                  <TableRow
                    key={product.id}
                    className={styles.clickableRow}
                    selected={selection.isSelected(product.id)}
                    tabIndex={0}
                    onClick={openDrawer}
                    onKeyDown={event => {
                      if (!isRowActivateKey(event)) return

                      event.preventDefault()
                      openDrawer()
                    }}
                  >
                    <TableCell onClick={event => event.stopPropagation()}>
                      <span onClickCapture={selection.registerShift}>
                        <Checkbox
                          checked={selection.isSelected(product.id)}
                          onChange={() => selection.toggle(product)}
                          aria-label={name}
                        />
                      </span>
                    </TableCell>

                    <TableCell>
                      <span className={styles.productCell}>
                        <ProductThumb url={catalog?.image_url} />
                        <span className={styles.productText}>
                          <span className={styles.productName} title={name}>
                            {name}
                          </span>
                          <span className={styles.productMeta}>
                            <span className={styles.metaSep}>
                              {product.identifier_type === 'sku'
                                ? 'SKU'
                                : 'EAN'}
                              :{code}
                            </span>
                            {!catalog && (
                              <>
                                <span
                                  className={styles.metaSep}
                                  aria-hidden="true"
                                >
                                  |
                                </span>
                                <span className={styles.metaEllipsis}>
                                  {t(
                                    'rm:advertiserProducts.table.noCatalogMatch'
                                  )}
                                </span>
                              </>
                            )}
                          </span>
                        </span>
                      </span>
                    </TableCell>

                    <TableCell>
                      {product.brand ? (
                        <Text
                          variant="body"
                          className={styles.brandCell}
                          title={product.brand}
                        >
                          {product.brand}
                        </Text>
                      ) : (
                        <Text variant="body" className={styles.noBrand}>
                          {t('rm:advertiserProducts.noBrand')}
                        </Text>
                      )}
                    </TableCell>

                    {canManage && (
                      <TableCell
                        align="end"
                        onClick={event => event.stopPropagation()}
                      >
                        <Menu
                          label={t('rm:advertiserProducts.actions.menu', {
                            product: name
                          })}
                          type="actions"
                          iconOnly
                        >
                          <MenuItem
                            critical
                            onClick={() => confirmRemove.ask([product])}
                          >
                            <IconTrash />
                            {t('rm:advertiserProducts.bulk.remove')}
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CatalogListCard>

        <BulkBar
          count={selection.selected.length}
          label={t('rm:advertiserProducts.selected', {
            count: selection.selected.length
          })}
          onClear={selection.clear}
          actions={bulkActions}
        />

        <UnassignedSuggestionsModal
          open={isUnassignedSuggestionsOpen}
          onClose={() => setIsUnassignedSuggestionsOpen(false)}
        />

        <AddProductsModal
          open={isAddOpen}
          defaultBrand={brands.length === 1 ? brands[0] : undefined}
          onClose={() => setIsAddOpen(false)}
        />

        <ImportCsvModal
          open={isImportOpen}
          onClose={() => setIsImportOpen(false)}
        />

        <LinkBrandModal
          open={isLinkOpen}
          products={selectedProducts}
          onClose={() => setIsLinkOpen(false)}
          onSuccess={selection.clear}
        />

        <confirmRemove.ConfirmationModal
          isOpen={confirmRemove.isOpen}
          title={t('rm:advertiserProducts.remove.confirm.title', {
            count: confirmRemove.items.length
          })}
          onClose={confirmRemove.close}
          confirmationButton={t('rm:advertiserProducts.remove.confirm.submit')}
          cancelButton={t('rm:button.Cancel')}
          submitLoading={removeProducts.isPending}
          onSubmit={handleRemove}
        >
          {t('rm:advertiserProducts.remove.confirm.description', {
            count: confirmRemove.items.length
          })}
        </confirmRemove.ConfirmationModal>
      </NtbGate>
    </div>
  )
}

export default AdvertiserProductsPage
